// ─────────────────────────────────────────────────────────────────────────────
// claimCrops — WHICH CROPS THE CLAIM TOOLS COVER, IN WHAT UNIT, AGAINST WHICH TEST.
//
// Kevin, 2026-09-30: *"Claims manager likely needs to account for all crops, not
// just corn soybeans and wheat."*
//
// The Claim Check crop picker read `GRAIN_CROPS` — the GRAIN MARKETING list, which
// is correctly three crops there because those are the ones with futures markets and
// cash contracts. Borrowing it for claims was the defect. Measured on the live book,
// CY2026 coverage carrying acres:
//
//     CORN 192,892 ac  $102.3M      POTATOES  3,947 ac  $7.5M   ← not offered
//     SOYBEANS 79,225  $28.7M       GRAPES    4,067 ac  $6.7M   ← not offered
//     WHEAT    29,290  $8.8M        ONIONS · DRY BEANS · CABBAGE · PROCESSING
//                                   BEANS · PEACHES · GRAIN SORGHUM · APPLES ·
//                                   OATS · TRITICALE
//
// Eleven crops and $16.3M of liability the claim screener could not look at — more
// than wheat's $8.8M. A crop that cannot be selected reads as a crop with no claim
// exposure, which is the quiet direction.
//
// ⚠ WIDENING THE LIST IS THE SMALL HALF. Two things differ per crop and both are
// wrong-by-default if you only add names to an array:
//
//   1. THE UNIT. Potatoes and onions are cwt/ac, dry beans lbs/ac, grapes and
//      processing beans ton/ac, corn silage ton/ac, everything else bu/ac. This repo
//      already records what happens when units are mixed — Lismore's Steuben corn
//      read 86.5 from ((154 bu + 18.9 ton)/2) on $2.28M of liability. A claim screen
//      that prints "bushels" over a potato guarantee is the same error in a label.
//
//   2. THE TEST. Measured over RMA's own published prices for CY2026:
//
//          commodity        projected price   HARVEST price
//          corn/soy/wheat        yes              YES
//          potatoes, grapes,     yes              NONE — 0 rows, every one
//          onions, dry beans,
//          cabbage, peaches,
//          apples
//
//      RMA publishes a harvest price for none of them, because they are not revenue
//      products. Their policies are APH/YP: the guarantee is in UNITS and a loss is
//      a yield shortfall valued at the ESTABLISHED price. There is no revenue
//      guarantee to compare against, and printing one would be inventing a product
//      the grower did not buy — the same class as drawing last year's coverage on
//      this year's SCO/ECO lattice.
//
// ⚠ THE DISCRIMINATOR IS THE PLAN, NOT THE CROP AND NOT WHETHER A HARVEST PRICE HAS
// LANDED YET. Both of the obvious alternatives are wrong:
//   * by crop — OATS and GRAIN SORGHUM carry BOTH in this book (oats: 6 YP rows and
//     1 RP row), so a crop-keyed table gives one of those growers the wrong test.
//   * by "does rma_prices hold a harvest price today" — harvest discovery closes in
//     the autumn, so CY2026 corn reads 906 of 2,280 rows populated. Mid-season that
//     test would silently reclassify corn as a yield product.
// The plan is what the grower bought, it is on every row, and it is stated before
// the season starts.
//
// Plan vocabulary MEASURED over CY2024-26, all 2,265 rows, no nulls:
//   RP 1018 · ECO-RP 683 · SCO-RP 316 · APH 183 · YP 49 · ECO-YP 14 · SCO-YP 2
// ─────────────────────────────────────────────────────────────────────────────

// ── THE UNIT ────────────────────────────────────────────────────────────────
// ⚠ ONE DEFINITION. `yieldUnit()` in App.jsx delegates here rather than keeping a
// second copy — this file already records three cases of a rule defined twice and
// drifting (the news handlers, the two guarantee engines, three normFarmerName
// copies). Keyed by crop, with corn's SILAGE TYPE as the one type-level exception,
// because corn is one crop insured as two products in two units.
const UNIT_BY_CROP = {
  POTATOES: "cwt",
  ONIONS: "cwt",
  CABBAGE: "cwt",
  "DRY BEANS": "lbs",
  "PROCESSING BEANS": "ton",
  GRAPES: "ton",
};

// Corn type 026 / GSS is SILAGE — tons, not bushels. Type 016 / GSG is grain.
// `normalizeCropType` lives in App.jsx; the two spellings are handled here so this
// module stays import-free and the checker can load and drive the shipped source.
export function isSilageType(typeCode) {
  const t = String(typeCode == null ? "" : typeCode).toUpperCase().trim();
  return t === "026" || t === "GSS" || t === "SILAGE" || t === "SILAGE-026";
}

// The unit a crop's production and guarantee are stated in.
// ⚠ AN UNRECOGNISED CROP RETURNS "bu" AND SAYS SO VIA `unitIsAssumed`. Bushels is
// the right default for the grains and orchard crops this book carries (apples 582-677
// and peaches 247 are both bu/ac), and a blank unit renders as a broken column. But an
// assumed unit must never be indistinguishable from a stated one, because the crop
// that arrives next could be measured in anything.
export function claimUnit(crop, typeCode) {
  if (isSilageType(typeCode)) return "ton";
  const c = String(crop == null ? "" : crop).toUpperCase().trim();
  return UNIT_BY_CROP[c] || "bu";
}

// Crops this book has carried whose unit is bushels, STATED rather than defaulted.
// Measured over CY2024-26 crop_coverage; a crop outside this set still renders in
// bushels and is flagged, never silently asserted.
// ⚠ DECLARED BEFORE ITS READER. A `const` below the function that closes over it is
// a temporal-dead-zone hazard this repo has a checker for, and a `const` after a
// return never initialises at all.
const KNOWN_BUSHEL_CROPS = new Set([
  "CORN", "SOYBEANS", "WHEAT", "WINTER WHEAT", "BARLEY", "OATS", "TRITICALE", "RYE",
  "GRAIN SORGHUM", "APPLES", "PEACHES",
]);

// True when `claimUnit` fell through to its default rather than stating a unit.
export function unitIsAssumed(crop, typeCode) {
  if (isSilageType(typeCode)) return false;
  const c = String(crop == null ? "" : crop).toUpperCase().trim();
  if (!c) return true;
  if (UNIT_BY_CROP[c]) return false;
  return !KNOWN_BUSHEL_CROPS.has(c);
}

// ── THE TEST ────────────────────────────────────────────────────────────────
// A revenue plan compares REVENUE (production x harvest price) against a revenue
// guarantee. A yield plan compares PRODUCTION against a production guarantee, and
// values the shortfall at the established price. They are different claims and the
// screen must not offer the wrong one.
//
// The ECO-/SCO- prefix is stripped first: those are county-index overlays riding on
// the base product, and an ECO-RP row is revenue exactly as its RP base is.
export function claimBasis(plan) {
  const raw = String(plan == null ? "" : plan).toUpperCase().trim();
  // ⚠ ONLY THE ECO-/SCO- PREFIXES ARE STRIPPED. `ARPI` is a whole plan name, not a
  // prefix — stripping it leaves an empty string, which this function would then have
  // to call unknown, so Area Revenue Protection would read as a plan we cannot
  // classify when it is plainly revenue. (Base-coverage callers filter it out as an
  // overlay before reaching here; it is classified anyway so a caller that does not
  // filter cannot get a wrong answer instead of no answer.)
  if (raw === "ARPI" || raw === "ARP") return "revenue";
  const p = raw.replace(/^(ECO|SCO)[-\s]*/, "");
  if (!p) return "unknown";
  // RP, RP-HPE (harvest price exclusion — still a revenue product; the guarantee
  // simply does not rise with the harvest price). RP-HPE is NOT in this book today;
  // it is recognised because RMA sells it, not because it was observed here.
  if (p === "RP" || /^RP[-\s]/.test(p)) return "revenue";
  if (p === "YP" || p === "APH") return "yield";
  // ⚠ UNKNOWN IS ITS OWN ANSWER, never folded into either. A plan nobody has seen
  // gets no claim test and a named reason — the alternative is showing a grower a
  // revenue guarantee their policy may not carry.
  return "unknown";
}

// The basis for a whole coverage, which may span several plans on one crop.
// ⚠ MIXED IS NOT A REVENUE COVERAGE. A farm carrying RP in one county and YP in
// another has no single revenue guarantee, so the revenue test is withheld and both
// halves are named. Collapsing to "revenue because one row is RP" would price a YP
// county against a harvest price it does not settle on.
export function coverageBasis(plans) {
  const seen = new Set((plans || []).map(claimBasis));
  seen.delete(undefined);
  if (seen.size === 0) return { basis: "unknown", plans: [], mixed: false };
  if (seen.size === 1) return { basis: [...seen][0], plans: [...new Set(plans)], mixed: false };
  return { basis: "mixed", plans: [...new Set(plans)], mixed: true, bases: [...seen] };
}

// ── THE CROP LIST ───────────────────────────────────────────────────────────
// ⚠ READ FROM THE BOOK, NEVER TYPED. A typed list is how eleven crops went missing
// for the life of this tool, and it is how the twelfth would go missing next season.
// `rows` are crop_coverage rows already narrowed to the crop year.
//
// Sorted by liability descending — the order an agent works a claim season in — with
// the crop name as a stable tie-break so the picker does not reshuffle between loads.
export function claimCropsInBook(rows) {
  const by = new Map();
  for (const r of rows || []) {
    const crop = String((r && r.crop) || "").toUpperCase().trim();
    if (!crop) continue;
    const ac = Number(r.acres) || 0;
    const lib = Number(r.liability) || 0;
    if (!by.has(crop)) by.set(crop, { crop, acres: 0, liability: 0, plans: new Set(), types: new Set() });
    const e = by.get(crop);
    e.acres += ac;
    e.liability += lib;
    if (r.plan) e.plans.add(String(r.plan).toUpperCase().trim());
    if (r.type_code != null || r.type != null) e.types.add(String(r.type_code || r.type).toUpperCase().trim());
  }
  return [...by.values()]
    .map((e) => {
      const plans = [...e.plans];
      const cb = coverageBasis(plans);
      return {
        crop: e.crop,
        acres: e.acres || null,
        liability: e.liability || null,
        plans,
        basis: cb.basis,
        mixed: !!cb.mixed,
        // The unit of the crop's BASE product. A corn coverage holding both grain and
        // silage rows is reported as `bu` with `hasSilage`, because the picker selects a
        // CROP and the split is made below it — not flattened into one unit here.
        unit: claimUnit(e.crop, null),
        unitAssumed: unitIsAssumed(e.crop, null),
        hasSilage: [...e.types].some(isSilageType),
      };
    })
    .sort((a, b) => (b.liability || 0) - (a.liability || 0) || a.crop.localeCompare(b.crop));
}

// ── THE CLAIMS MANAGER CAMPAIGNS ────────────────────────────────────────────
// A campaign is (crop year, claim type). Prevent Plant is its own roster off
// pp_eligibility; every other campaign is one crop's harvest.
//
// ⚠ THE THREE GRAIN KEYS ARE PRESERVED EXACTLY. `claimType` is held in component
// state and read back from a stored default, and the board's own per-campaign logic
// (silage appraisals, the John Deere yield cross-check, the chop-notice lane) keys on
// them. Renaming them to fit a derived scheme would break a stored campaign and every
// one of those lanes for no gain — so the known three keep their key, label and emoji,
// and a crop the book starts carrying gets a derived campaign beside them.
const KNOWN_HARVEST = {
  WHEAT: { key: "WHEAT_HARVEST", label: "Wheat Harvest", emoji: "🌾" },
  CORN: { key: "CORN_HARVEST", label: "Corn Harvest", emoji: "🌽" },
  SOYBEANS: { key: "SOY_HARVEST", label: "Soybean Harvest", emoji: "🫘" },
};
export const PP_CAMPAIGN_KEY = "PREVENT_PLANT";

// `crops` is the output of claimCropsInBook for the campaign's crop year.
// Returns the campaign map in DISPLAY ORDER: Prevent Plant first (it is the spring
// roster and its own product), then each crop by liability descending.
//
// ⚠ AN EMPTY BOOK STILL RETURNS THE THREE GRAIN CAMPAIGNS. The board renders before
// its crops have loaded, and a campaign bar that flickers empty reads as a broken
// page. They are the placeholder, never the ceiling.
export function claimCampaigns(crops) {
  const out = {};
  out[PP_CAMPAIGN_KEY] = { label: "Prevent Plant", kind: "pp", crop: null, emoji: "🌧️" };
  const list = (crops || []).length
    ? crops
    : ["WHEAT", "CORN", "SOYBEANS"].map((crop) => ({ crop, liability: null, basis: null, unit: "bu" }));
  for (const c of list) {
    const known = KNOWN_HARVEST[c.crop];
    const key = known ? known.key : `HARVEST_${c.crop.replace(/[^A-Z0-9]+/g, "_")}`;
    out[key] = {
      label: known ? known.label : `${claimCropLabel(c.crop)} Harvest`,
      kind: "harvest",
      // The crop is carried in the app's own Title Case, because every downstream
      // consumer (JD_CROP, RMA_ADM_CROPS, the Claim Check drill-in) is keyed that way.
      crop: claimCropLabel(c.crop),
      emoji: known ? known.emoji : "🌱",
      basis: c.basis || null,
      unit: c.unit || "bu",
      liability: c.liability == null ? null : c.liability,
      derived: !known,
    };
  }
  return out;
}

// Title case for display — the pickers elsewhere in the app show "Corn", not "CORN".
export function claimCropLabel(crop) {
  const c = String(crop == null ? "" : crop).trim();
  if (!c) return "";
  return c.toLowerCase().replace(/(^|[\s-])([a-z])/g, (m, sep, ch) => sep + ch.toUpperCase());
}

// What the screen says when the revenue test is withheld. One sentence, one place,
// so the Claim Check page and the Claims Manager board cannot explain it differently.
export function basisNote(basis, crop) {
  const name = claimCropLabel(crop) || "This crop";
  if (basis === "revenue") return null;
  if (basis === "yield")
    return `${name} is insured on a yield plan (APH/YP), so there is no revenue guarantee and RMA publishes no harvest price for it. The claim test is production against the production guarantee; a shortfall is valued at the established price.`;
  if (basis === "mixed")
    return `${name} carries both revenue and yield plans on this farm, so there is no single revenue guarantee. The production comparison below holds for every county; the revenue comparison is withheld rather than applied to the yield-plan counties.`;
  return `The insurance plan on ${name} is not one this tool recognises, so neither the revenue nor the production test is applied. Read the policy before treating anything here as a guarantee.`;
}
